import type { MetaFunction } from "react-router";

import { LegalDocument } from "@/legal/document";

export const meta: MetaFunction = () => [
  { title: `Privacy Policy | ${publicEnv.siteTitle}` },
];

// Document the distinction between local photo processing and the limited
// network requests needed to deliver, measure, and support the browser tool.
//
export default function Privacy() {
  return (
    <LegalDocument
      description="What stays on your device, which limited network requests occur, and the choices available when you use privacy.photos."
      title="Privacy Policy"
      updated="August 11, 2026"
    >
      <section>
        <h2>1. The short version</h2>
        <p>
          Your selected photos, crops, face-analysis results, and generated
          print image stay in your browser. They are not uploaded to
          privacy.photos, our analytics, Hugging Face, or Stripe. Network
          requests still occur to load the website and machine-learning model
          files, optionally measure page use, and open an external support link
          if you choose it.
        </p>
      </section>

      <section>
        <h2>2. Scope and controller</h2>
        <p>
          This Policy describes how {publicEnv.siteLegalEntity}
          (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) handles
          personal information through privacy.photos. It does not govern
          Hugging Face, Stripe, a print service, a government authority, or
          another site you visit from a link.
        </p>
      </section>

      <section>
        <h2>3. Photos and local processing</h2>
        <p>
          When you select a photo, browser code reads it into temporary memory.
          Format conversion, thumbnails, background segmentation, face analysis,
          positioning, cropping, tiling, preview, and download occur on your
          device. The browser may create temporary object or data URLs
          containing the image. The Service does not submit the photo, its file
          name, form values, analysis, or output to our server.
        </p>
        <p>
          Photo working state normally disappears when you reload or close the
          page. A downloaded output remains wherever your browser, operating
          system, backup provider, shared device, or print service stores it.
          Use their controls to delete those copies.
        </p>
      </section>

      <section>
        <h2>4. Model downloads and browser cache</h2>
        <p>
          The first use of background removal or automatic face positioning can
          cause your browser to download machine-learning model files from
          Hugging Face. Hugging Face receives an ordinary network request, which
          can include your IP address, browser details, requested model, and
          request time, but the model runs locally and the request does not
          include your photo.
        </p>
        <p>
          Your browser may cache model files, including in IndexedDB or its
          ordinary web cache, to avoid downloading them again. These cached
          files are model code and weights, not your selected photo. You can
          remove them by clearing site data and browser caches.
        </p>
      </section>

      <section>
        <h2>5. Website requests and technical logs</h2>
        <p>
          Like most websites, our server and infrastructure receive the IP
          address, request time, requested path, response status, referrer, and
          browser or device information needed to deliver and secure the site.
          We may retain limited logs for reliability, security, abuse
          prevention, and troubleshooting, then delete or aggregate them when
          they are no longer reasonably needed.
        </p>
      </section>

      <section>
        <h2>6. Limited audience measurement</h2>
        <p>
          We may enable Umami to understand aggregate page use and performance.
          The configured tracker excludes URL searches and fragments, respects
          the browser&apos;s Do Not Track setting, does not use analytics
          cookies, and does not load session replay. We do not send selected
          photos, photo-derived values, or form values to analytics.
        </p>
        <p>
          When enabled, measurement may include the site and page path,
          referrer, browser and device category, approximate location derived
          from an IP address, performance data, and event time. The
          deployment&apos;s analytics provider processes that request only to
          operate audience measurement for us.
        </p>
      </section>

      <section>
        <h2>7. Preferences and communications</h2>
        <p>
          A light or dark theme preference and related client hints may be
          stored in your browser or an essential preference cookie. Blocking or
          clearing them resets those choices but does not prevent local photo
          processing.
        </p>
        <p>
          If you contact us, we receive your contact details, message, and any
          information you choose to include. Do not email photos or identity
          documents when a description of the issue is sufficient.
        </p>
      </section>

      <section>
        <h2>8. Voluntary support links</h2>
        <p>
          If you choose an external support link, Stripe or the linked payment
          page receives the information ordinarily disclosed by your browser and
          any contact, billing, and payment details you provide there. We may
          receive a contribution record and payment status but do not receive
          complete payment-card numbers. Merely using the photo tool does not
          send information to Stripe.
        </p>
      </section>

      <section>
        <h2>9. How we use information</h2>
        <p>We use the limited information we receive to:</p>
        <ul>
          <li>deliver the site and remember requested preferences;</li>
          <li>understand aggregate use and improve performance;</li>
          <li>respond to messages and support requests;</li>
          <li>process and reconcile voluntary contributions;</li>
          <li>
            prevent abuse, diagnose failures, and protect the Service; and
          </li>
          <li>comply with law and enforce our agreements.</li>
        </ul>
        <p>
          We do not sell personal information, share it for cross-context
          behavioral advertising, use photos for model training, or make
          decisions with legal or similarly significant effects based solely on
          automated processing.
        </p>
      </section>

      <section>
        <h2>10. How we disclose information</h2>
        <p>We may disclose received information to:</p>
        <ul>
          <li>
            providers for hosting, security, audience measurement, model-file
            delivery, and payment processing;
          </li>
          <li>
            advisers or authorities when reasonably necessary to comply with
            law, protect rights and safety, or investigate abuse; and
          </li>
          <li>
            a successor in a merger, financing, reorganization, or sale, subject
            to appropriate confidentiality and notice.
          </li>
        </ul>
        <p>
          Because we never receive your selected photo through the tool, we
          cannot disclose that photo from our servers.
        </p>
      </section>

      <section>
        <h2>11. Legal bases</h2>
        <p>
          Where data-protection law requires a legal basis, we process service
          delivery and requested support information to perform our contract
          with you; security, limited audience measurement, reliability, and
          improvement data for our legitimate interests; information needed to
          satisfy legal obligations; and information based on consent where we
          specifically request it. You may withdraw consent for future
          processing at any time, without affecting earlier processing.
        </p>
      </section>

      <section>
        <h2>12. Retention and international processing</h2>
        <p>
          We retain technical, analytics, support, contribution, security, and
          dispute records only as long as reasonably necessary for their purpose
          and legal obligations, then delete or de-identify them. We do not
          retain photo working data because we do not receive it.
        </p>
        <p>
          We and our providers may process received information in the United
          States and other countries whose laws differ from those where you
          live. Where required, we use a recognized transfer mechanism or
          another lawful safeguard. Contact us to ask about safeguards
          applicable to your information.
        </p>
      </section>

      <section>
        <h2>13. Security</h2>
        <p>
          Keeping photo computation in your browser reduces transmission risk.
          We also use reasonable measures designed to protect the limited
          information we receive. No browser, device, network, or storage system
          is completely secure. Protect shared devices and review where your
          downloads and backups are saved.
        </p>
      </section>

      <section>
        <h2>14. Your rights and choices</h2>
        <p>
          Depending on where you live, you may have rights to access, correct,
          delete, restrict, object to, or receive a portable copy of personal
          information, and to appeal a denied request. You may also complain to
          your local data-protection authority. You have the right to object to
          processing based on legitimate interests where applicable.
        </p>
        <p>
          Submit a request to{" "}
          <a href={`mailto:${publicEnv.siteSupportEmail}`}>
            {publicEnv.siteSupportEmail}
          </a>
          . We may verify your identity and authority before completing it. We
          will not discriminate against you for exercising a privacy right.
          Because we do not sell personal information or share it for
          cross-context behavioral advertising, there is no sale or targeted-ad
          opt-out to exercise.
        </p>
      </section>

      <section>
        <h2>15. Children</h2>
        <p>
          The Service is not directed to children under 13. A parent or guardian
          should supervise any child&apos;s use, including the handling of photo
          files and downloads. If you believe a child sent us personal
          information outside the local tool, contact us so we can investigate
          and delete it where appropriate.
        </p>
      </section>

      <section>
        <h2>16. Changes and contact</h2>
        <p>
          We may update this Policy as the Service or law changes. We will post
          the new effective date and provide reasonable notice of material
          changes. Questions and privacy requests may be sent to{" "}
          <a href={`mailto:${publicEnv.siteSupportEmail}`}>
            {publicEnv.siteSupportEmail}
          </a>
          .
        </p>
      </section>
    </LegalDocument>
  );
}
